import { deleteDoc, doc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { ulid } from 'ulid';
import { getDb } from '@talk2nick/shared';
import { tokenizeForSearch, type Message, type MessageType } from '@talk2nick/core';

/** 목록 미리보기 길이 상한 (플랫폼 규칙) */
const PREVIEW_MAX = 120;

/**
 * 방별 사라지는 메시지 TTL 캐시 (PLAN 0-B 변경 2).
 *
 * Rules가 `expiresAt = 서버시각 + TTL` 범위를 강제하므로 클라이언트가 값을 만들어야 하는데,
 * 그 값을 모든 전송 경로에 관통시키면(Composer → ChatRoom → messages/mediaUpload) 시그니처가
 * 다 죽는다. 대신 **방 문서 구독이 TTL을 여기다 내려놓고**, 전송은 여기서 읽는다.
 */
const ttlCache = new Map<string, number>();

export function rememberConvoTtl(convoId: string, ttlSeconds: number): void {
  ttlCache.set(convoId, ttlSeconds);
}

/** 전송 시 붙일 expiresAt. TTL이 꺼져 있으면 필드 자체를 쓰지 않는다(Rules가 필드 부재를 요구) */
export function expiresAtField(convoId: string): { expiresAt?: Date } {
  const ttl = ttlCache.get(convoId) ?? 0;
  return ttl > 0 ? { expiresAt: new Date(Date.now() + ttl * 1000) } : {};
}

/** 새 텍스트 메시지에 붙는 부가 필드 (답장/전달) */
interface TextMessageExtras {
  replyTo?: string;
  /** 전달된 메시지의 원 발신자 (PLAN 5-4-1장 "전달됨" 라벨) */
  forwardedFrom?: string;
}

/**
 * 텍스트 메시지 쓰기의 단일 경로 (전송 / 전달 공용).
 *
 * `messageId`는 클라이언트가 만든 ULID다 — 오프라인 큐가 재시도해도 같은 문서에
 * 쓰므로 중복이 생기지 않는다(idempotent). Firestore는 로컬 write를 즉시 캐시에
 * 반영하고 `hasPendingWrites`로 서버 확인 여부를 알려주므로, 낙관적 UI를 위한
 * 별도 스토어가 필요 없다 (PLAN 5-8-5장).
 */
async function writeTextMessage(
  convoId: string,
  senderId: string,
  rawText: string,
  extras: TextMessageExtras,
): Promise<string> {
  const text = rawText.trim();
  if (!text) throw new Error('empty-message');

  const db = getDb();
  const messageId = ulid();

  await setDoc(doc(db, 'convos', convoId, 'messages', messageId), {
    type: 'text',
    text,
    senderId,
    searchTokens: tokenizeForSearch(text),
    reactions: {},
    reactionCount: 0,
    clientCreatedAt: Date.now(),
    sourceDevice: 'web',
    createdAt: serverTimestamp(),
    ...expiresAtField(convoId),
    ...extras,
  });

  // 목록 미리보기는 비정규화 필드다 — 실패해도 메시지 자체는 이미 전송됐다.
  // messageId를 함께 넣어 인앱 배너가 그 메시지로 정확히 이동할 수 있게 한다 (5-8-4장).
  void updateDoc(doc(db, 'convos', convoId), {
    lastMessage: {
      text: text.slice(0, PREVIEW_MAX),
      senderId,
      type: 'text',
      messageId,
      createdAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  }).catch((error: unknown) => {
    console.error('[talk2nIcK] 목록 미리보기 갱신 실패:', error);
  });

  return messageId;
}

/** 텍스트 전송 (PLAN 5-4장). `replyTo`는 인용할 메시지 ID */
export function sendText(
  convoId: string,
  senderId: string,
  text: string,
  replyTo?: string,
): Promise<string> {
  return writeTextMessage(convoId, senderId, text, replyTo ? { replyTo } : {});
}

/** 전달 (PLAN 5-4-1장) — 원 발신자를 남겨 버블에 "전달됨" 라벨을 붙인다 */
export function forwardMessage(
  targetConvoId: string,
  senderId: string,
  text: string,
  originalSenderId: string,
): Promise<string> {
  return writeTextMessage(targetConvoId, senderId, text, { forwardedFrom: originalSenderId });
}

/** 시스템 알림 메시지 (입장 승인 등) */
export async function sendSystemMessage(convoId: string, text: string): Promise<string> {
  const db = getDb();
  const messageId = ulid();
  await setDoc(doc(db, 'convos', convoId, 'messages', messageId), {
    type: 'system',
    text,
    clientCreatedAt: Date.now(),
    createdAt: serverTimestamp(),
    reactions: {},
    reactionCount: 0,
  });
  return messageId;
}

/**
 * 메시지 편집 (PLAN 5-4-1장).
 * 15분 제한과 본인 여부는 서버 Rules가 최종 판정한다 — 넘으면 서버가 거부하고
 * 로컬 write가 롤백된다 (PLAN 5-8-5 낙관적 업데이트).
 * `searchTokens`를 다시 만들어야 검색 결과가 옛 본문을 가리키지 않는다.
 */
export async function editMessage(
  convoId: string,
  messageId: string,
  rawText: string,
): Promise<void> {
  const text = rawText.trim();
  if (!text) throw new Error('empty-message');
  await updateDoc(doc(getDb(), 'convos', convoId, 'messages', messageId), {
    text,
    searchTokens: tokenizeForSearch(text),
    editedAt: serverTimestamp(),
  });
}

/**
 * 편집은 본문만 바꾼다 — 목록 미리보기도 최신 본문으로 맞춘다.
 * `updatedAt`을 다시 쓰므로 목록 정렬이 위로 올라온다 (마지막 대화가 편집된 경우).
 */
export async function refreshConvoPreview(
  convoId: string,
  text: string,
  senderId: string,
  messageId: string,
): Promise<void> {
  // 점 경로로 쓴다 — lastMessage.reactions(리액션 요약)를 지우지 않기 위함
  await updateDoc(doc(getDb(), 'convos', convoId), {
    'lastMessage.text': text.trim().slice(0, PREVIEW_MAX),
    'lastMessage.senderId': senderId,
    'lastMessage.type': 'text',
    'lastMessage.messageId': messageId,
    'lastMessage.createdAt': serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** 메시지 삭제 — Rules가 본인 15분 이내 또는 관리자를 강제한다 */
export async function deleteMessage(convoId: string, messageId: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'convos', convoId, 'messages', messageId));
}

/**
 * 읽음 처리 — 대화방을 열어 둔 동안 마지막 메시지 시각까지 올린다.
 * 상대방의 틱이 여기서 `delivered`/`read`로 바뀐다 (PLAN 5-4장).
 */
export async function markRead(
  convoId: string,
  uid: string,
  lastReadAt: number,
  unreadCount = 0,
): Promise<void> {
  await updateDoc(doc(getDb(), 'convos', convoId, 'members', uid), {
    lastReadAt: new Date(lastReadAt),
    unreadCount,
  });
}

/** 로컬 캐시에만 있는(서버 미확인) 메시지도 안정적으로 정렬하기 위한 키 */
export function sortKeyOf(message: Message): number {
  return message.createdAt ? message.createdAt.toDate().getTime() : message.clientCreatedAt;
}

/**
 * 즐겨찾기/고정 스냅샷에 필요한 최소 메시지 표현.
 * 전체 Message 없이도(예: 서버에서 다시 읽어온 스니펫) 저장할 수 있게 하려는 것.
 */
export interface MessageSnapshot {
  id: string;
  senderId: string;
  type: MessageType;
  text?: string;
  createdAt?: { toDate(): Date };
  clientCreatedAt: number;
}

/** MessageSnapshot용 정렬 키 — Message도 구조적으로 이 함수를 쓸 수 있다 */
export function snapshotKeyOf(message: MessageSnapshot): number {
  return message.createdAt ? message.createdAt.toDate().getTime() : message.clientCreatedAt;
}
